import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { EmployeePTO, PTOConfig, TimeOffRate } from '../../../../../models/paid-time-off';
import { FormArray, FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../../../utils/datepicker-value-parser';
import {
  CannotActivateInactivePlan,
  MustBeUnique,
  MustHaveAtLeastOne,
  NoDuplicateAccrualPeriods
} from './RateValidators';
import { PeopleService } from '../../../../../services/people.service';
import { getObjectDeltas } from '../../../../../utils/object-deltas';
import { SharedService } from '../../../../../services/shared.service';
import { AuditService } from '../../../../../services/audit.service';
import { Person } from '../../../../../models/person';
import { ToastService } from '../../../../../services/toast.service';

@Component({
  selector: 'lib-employee-pto-config',
  templateUrl: './employee-pto-config.component.html',
  styleUrls: ['./employee-pto-config.component.css'],
  standalone: false
})
export class EmployeePtoConfigComponent implements OnInit {
  @Input()
  public ptos: EmployeePTO[] = [];

  @Input()
  public readonly: boolean = false;

  @Input()
  public person: Person;

  @Input()
  public clientCode: string;

  @Input()
  public ptoConfigs: PTOConfig[] = [];

  @Output()
  public modalClosed: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public existingModel: EmployeePTO | null = null;

  public showInactivePlan: boolean = false;

  public timeOffModalForm: FormGroup;

  public planChosen: boolean = false;
  public chosenPlan: PTOConfig;

  @ViewChild('ptoConfigModal', { static: true })
  public modalContent: TemplateRef<any>;

  private modalRef: NgbModalRef;

  public get employeeRates() {
    return this.timeOffModalForm.get('EmployeeRates') as FormArray;
  }

  private readonly toastService = inject(ToastService);

  constructor(
    private fb: FormBuilder,
    private ngModalService: NgbModal,
    private peopleService: PeopleService,
    private sharedService: SharedService,
    private auditService: AuditService
  ) {}

  ngOnInit() {
    let anniversaryDate = null;
    if (this.existingModel != null) {
      this.planChosen = true;
      this.chosenPlan = this.ptoConfigs.find(x => x.Id == this.existingModel.Id);
      if (typeof this.existingModel.AnniversaryDate == 'string') {
        anniversaryDate = new Date(this.existingModel.AnniversaryDate);
      }
    }

    this.timeOffModalForm = this.fb.group(
      {
        Id: this.fb.control(
          { value: this.existingModel?.Id ?? null, disabled: this.readonly },
          Validators.required
        ),
        IntegrityHash: this.fb.control(this.existingModel?.IntegrityHash),
        AnniversaryDate: this.fb.control(
          {
            value: formatDatePickerValue(anniversaryDate ?? this.person.HireDate),
            disabled: this.readonly
          },
          Validators.required
        ),
        CarryOver: this.fb.control(
          { value: this.existingModel?.CarryOver.toFixed(2) ?? '0.00', disabled: this.readonly },
          [Validators.required, Validators.max(9000)]
        ),
        Accrued: this.fb.control(
          { value: this.existingModel?.AccruedHours.toFixed(2) ?? '0.00', disabled: this.readonly },
          [Validators.required, Validators.max(9000)]
        ),
        Used: this.fb.control(
          { value: this.existingModel?.UsedHours.toFixed(2) ?? '0.00', disabled: this.readonly },
          [Validators.required, Validators.max(9000)]
        ),
        Balance: this.fb.control(
          { value: this.existingModel?.Balance.toFixed(2) ?? '0.00', disabled: this.readonly },
          [Validators.required, Validators.max(9000)]
        ),
        Active: this.fb.control({
          value: this.existingModel?.IsActive ?? this.chosenPlan?.IsActive,
          disabled: this.readonly
        }),
        OverrideClientRates: this.fb.control({
          value: this.existingModel?.OverrideClientRates ?? false,
          disabled: this.readonly
        }),
        EmployeeRates: this.fb.array([])
      },
      {
        validators: [CannotActivateInactivePlan(this.ptoConfigs), MustBeUnique(this.ptoConfigs)]
      }
    );
    if (this.existingModel != null) {
      if (this.existingModel.OverrideClientRates) {
        this.employeeRates.setValidators([NoDuplicateAccrualPeriods, MustHaveAtLeastOne]);
        this.existingModel.TimeOffRates.forEach(x => this.addNewRate(x));
      }
    }
    this.timeOffModalForm.get('Id').valueChanges.subscribe(value => {
      this.planChosen = true;
      this.chosenPlan = this.ptoConfigs.find(x => x.Id == value);
      this.timeOffModalForm.get('Active').setValue(this.chosenPlan.IsActive);
    });

    this.timeOffModalForm.get('OverrideClientRates').valueChanges.subscribe(val => {
      if (val) {
        this.employeeRates.setValidators([NoDuplicateAccrualPeriods, MustHaveAtLeastOne]);
        this.chosenPlan.TimeoffRates.forEach(rate => {
          this.addNewRate(rate, false);
        });
      } else {
        this.employeeRates.clear();
        this.employeeRates.clearValidators();
      }
      this.employeeRates.updateValueAndValidity();
    });
    this.timeOffModalForm.get('Active').valueChanges.subscribe(val => {
      const plan = this.chosenPlan;
      if (val && !plan.IsActive) {
        this.showInactivePlan = true;
        setTimeout(() => {
          this.timeOffModalForm.get('Active').setValue(false);
          this.timeOffModalForm.updateValueAndValidity();
        }, 0);
      }
    });

    this.modalRef = this.ngModalService.open(this.modalContent, {
      size: 'xl',
      windowClass: 'centered',
      backdrop: 'static',
      keyboard: false
    });
    this.modalRef.result.then(
      // close
      result => {},
      // dismiss
      () => this.modalClosed.emit({ Operation: 'dismiss', Result: false })
    );
  }

  public isAlreadyAdded(ptoConfig) {
    return this.ptos.some(x => x.Id == ptoConfig.Id);
  }

  public addNewRate(initialModel: TimeOffRate | null = null, setSequenceNumber = true) {
    let sequenceNumber = 0;
    if (setSequenceNumber) {
      sequenceNumber = initialModel?.SequenceNumber ?? 0;
    }

    const group = this.fb.group({
      SequenceNumber: this.fb.control(sequenceNumber),
      AccrueAfter: this.fb.control(initialModel?.AccrueAfter, [
        Validators.required,
        Validators.min(0)
      ]),
      AccruePeriod: this.fb.control(initialModel?.AccruePeriod ?? 0, [
        Validators.required,
        Validators.min(0)
      ]),
      Rate: this.fb.control(initialModel?.Rate, [Validators.required, Validators.min(0)]),
      AccrualPeriodCap: this.fb.control(initialModel?.AccrualPeriodCap, [
        Validators.required,
        Validators.min(0)
      ]),
      AccrualTotalCap: this.fb.control(initialModel?.AccrualTotalCap, [
        Validators.required,
        Validators.min(0)
      ]),
      UsedTotalCap: this.fb.control(initialModel?.UsedTotalCap, [
        Validators.required,
        Validators.min(0)
      ]),
      BalanceTotalCap: this.fb.control(initialModel?.BalanceTotalCap, [
        Validators.required,
        Validators.min(0)
      ]),
      BalanceCarryOverCap: this.fb.control(initialModel?.BalanceCarryOverCap ?? 0)
    });
    if (this.chosenPlan.BalanceRule == 0) {
      group.get('BalanceCarryOverCap').setValidators([Validators.required, Validators.min(0)]);
    }
    this.employeeRates.push(group);
  }

  public removeRate(index: number) {
    this.employeeRates.removeAt(index);
  }

  public formSubmit() {
    const formVal = this.timeOffModalForm.value;
    const _pto: EmployeePTO = {
      Id: formVal.Id,
      IntegrityHash: formVal.IntegrityHash,
      CarryOver: parseFloat(formVal.CarryOver),
      AccruedHours: parseFloat(formVal.Accrued),
      UsedHours: parseFloat(formVal.Used),
      Balance: parseFloat(formVal.Balance),
      IsActive: formVal.Active,
      AnniversaryDate: parseDatePickerValue(formVal.AnniversaryDate),
      OverrideClientRates: formVal.OverrideClientRates,
      TimeOffRates: formVal.EmployeeRates
    };
    const obs =
      this.existingModel != null
        ? this.peopleService.updateEmployeePTO(this.clientCode, this.person.Id, _pto)
        : this.peopleService.createEmployeePTO(this.clientCode, this.person.Id, _pto);

    obs.subscribe(resp => {
      this.toastService.show('Save successful');

      this.sharedService.personEditsSaved.next();

      let deltas = getObjectDeltas(this.existingModel, resp.Model, [
        'IntegrityHash',
        'TimeOffRates',
        'Id'
      ]);
      const planName = JSON.stringify({
        oldVal: this.chosenPlan.Name,
        newVal: this.chosenPlan.Name
      });
      deltas = {
        ...deltas,
        PlanName: planName
      };
      this.auditService.auditEmployee(
        `employee-pto-updated`,
        {
          ClientCode: this.clientCode,
          PersonId: this.person.Id,
          EmployeeNumber: this.person.EmployeeId,
          EmployeeName: `${this.person.LastName}, ${this.person.FirstName}`
        },
        deltas
      );
      if (resp.Model.TimeOffRates.length > 0) {
        for (let i = 0; i < resp.Model.TimeOffRates.length; i++) {
          let oldRate = null;
          if ((this.existingModel?.TimeOffRates?.length ?? 0) >= i + 1) {
            oldRate = this.existingModel.TimeOffRates[i];
          }
          const rateDelta = getObjectDeltas(oldRate, resp.Model.TimeOffRates[i], []);
          this.auditService.auditEmployee(
            `employee-pto-rate-updated`,
            {
              ClientCode: this.clientCode,
              PersonId: this.person.Id,
              EmployeeNumber: this.person.EmployeeId,
              EmployeeName: `${this.person.LastName}, ${this.person.FirstName}`
            },
            rateDelta
          );
        }
      }
      if (
        resp.Model.TimeOffRates.length == 0 &&
        (this.existingModel?.TimeOffRates?.length ?? 0) != 0
      ) {
        //we've deleted rates.
        for (let i = 0; i < this.existingModel.TimeOffRates.length; i++) {
          const rateDelta = getObjectDeltas(this.existingModel.TimeOffRates[i], null, []);
          this.auditService.auditEmployee(
            `employee-pto-rate-removed`,
            {
              ClientCode: this.clientCode,
              PersonId: this.person.Id,
              EmployeeNumber: this.person.EmployeeId,
              EmployeeName: `${this.person.LastName}, ${this.person.FirstName}`
            },
            rateDelta
          );
        }
      }
      this.modalRef.dismiss();
    });
  }
}
